import React from "react";
import { Timeline, Spin, Typography, Tooltip, Tag, Card } from "antd";
import {
  SwapOutlined,
  RollbackOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStateColor = (estado) => {
  switch (estado?.toLowerCase()) {
    case "activo":
      return "green";
    case "facturado":
      return "blue";
    case "prestado":
      return "orange";
    case "anulado":
      return "red";
    default:
      return "default";
  }
};

const SerialHistory = ({ serialHistory, selectedSerial, loading }) => {
  const getIconAndColor = (tipoId) => {
    switch (tipoId) {
      case 1:
        return {
          icon: <FileTextOutlined />,
          color: "blue",
          text: "Facturación",
        };
      case 2:
        return {
          icon: <RollbackOutlined />,
          color: "red",
          text: "Devolución de Factura",
        };
      case 3:
        return {
          icon: <SwapOutlined />,
          color: "orange",
          text: "Préstamo",
        };
      case 4:
        return {
          icon: <SwapOutlined />,
          color: "green",
          text: "Devolución de Préstamo",
        };
      case "state_change":
        return {
          icon: <SyncOutlined />,
          color: "purple",
          text: "Cambio de Estado",
        };
      default:
        return {
          icon: <SyncOutlined />,
          color: "default",
          text: "Otro",
        };
    }
  };

  const getTimelineItems = () => {
    const items = [];

    // Add current state
    items.push({
      id: "current-state",
      date: new Date(),
      tipo: { id: "state_change" },
      isState: true,
      estado: selectedSerial?.estado?.estado,
      text: `Estado actual: ${selectedSerial?.estado?.estado}`,
    });

    // Add history items and their corresponding state changes
    if (serialHistory) {
      serialHistory.forEach((item) => {
        // Add the history event
        items.push({
          ...item,
          date: new Date(item.date_created),
          isHistoryEvent: true,
        });

        // Add the state change if it exists
        if (item.estado_anterior && item.estado_nuevo) {
          items.push({
            id: `state-${item.id}`,
            date: new Date(item.date_created),
            tipo: { id: "state_change" },
            isStateChange: true,
            estado_anterior: item.estado_anterior,
            estado_nuevo: item.estado_nuevo,
            date_created: item.date_created,
          });
        }
      });
    }

    // Sort by date, most recent first
    return items.sort((a, b) => b.date - a.date);
  };

  if (loading) {
    return <Spin size="large" />;
  }

  if (!serialHistory || serialHistory.length === 0) {
    return (
      <Text type="secondary">No hay historial disponible para este serial</Text>
    );
  }

  return (
    <Card>
      <Timeline mode="left">
        {getTimelineItems().map((item, index) => {
          const { icon, color, text } = getIconAndColor(item.tipo?.id);
         
          return (
            <Timeline.Item
              key={item.id || index}
              dot={item.isState ? <CheckCircleOutlined /> : icon}
              color={item.isStateChange ? "purple" : color}
            >
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Tag color={color}>
                    {item.isState
                      ? item.text
                      : item.isStateChange
                      ? "Cambio de Estado"
                      : text}
                  </Tag>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                 
                  {item.isState ? (
                    <>
                      <Text type="secondary">
                        Estado:{" "}
                        <Tag color={getStateColor(item.estado)}>
                          {item.estado}
                        </Tag>
                      </Text>
                      {item.estado === "prestado" && (
                        <Text type="secondary">
                          Prestado a:{" "}
                          {
                            selectedSerial?.prestamo[0]?.prestamos_id?.vendedor
                              ?.first_name
                          }{" "}
                          {
                            selectedSerial?.prestamo[0]?.prestamos_id?.vendedor
                              ?.last_name
                          }
                        </Text>
                      )}
                     
                    </>
                  ) : item.isStateChange ? (
                    <>
                      <Text type="secondary">
                        Estado anterior:{" "}
                        <Tag color={getStateColor(item.estado_anterior)}>
                          {item.estado_anterior}
                        </Tag>
                      </Text>
                      <Text type="secondary">
                        Nuevo estado:{" "}
                        <Tag color={getStateColor(item.estado_nuevo)}>
                          {item.estado_nuevo}
                        </Tag>
                      </Text>
                    </>
                  ) : (
                    <>
                     { item?.factura_n && (
                    <Text type="secondary">
                      Facturado por: {item.factura_n.user_created?.first_name}{" "}
                      {item.factura_n.user_created?.last_name}
                    </Text>
                  )}
                      {item.factura_n?.id && (
                        <Text type="secondary">
                          Factura N°: {item.factura_n.id}
                        </Text>
                      )}
                      {item.prestamo?.id && (
                        <Text type="secondary">
                          Préstamo N°: {item.prestamo.id}
                        </Text>
                      )}
                    </>
                  )}

                  {item.user_created && (
                    <Text type="secondary">
                      Creado por: {item.user_created.first_name}{" "}
                      {item.user_created.last_name}
                    </Text>
                  )}
                </div>

                <Text
                  type="secondary"
                  style={{ display: "block", marginTop: 8 }}
                >
                  <ClockCircleOutlined style={{ marginRight: 8 }} />
                  {formatDate(item.date)}
                </Text>
              </Card>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </Card>
  );
};

export default SerialHistory;
